import {
  useQueries,
  type QueryFunctionContext,
  type UseQueryResult,
} from '@tanstack/react-query';
import type { Aggregation } from '../../types/domain';
import {
  dataApiClient,
  type AgentAggregationDimension,
  type TripAggregationDimension,
} from '../../infrastructure/dataApi/client';
import { useCurrentSimDataId } from '../SimViewer/useCurrentSimDataId';
import { useApiDataFilter } from '../SimViewer/useApiDataFilter';
import { useSchemaComparison } from '../SimViewer/useSchemaComparison';

type AttributeAggregationDimension = Extract<
  AgentAggregationDimension | TripAggregationDimension,
  | 'gender'
  | 'age'
  | 'nationality'
  | 'end_activity_type'
  | 'longest_distance_mode'
>;

type UseAttributeAggregationComparisonsData = {
  isLoading: boolean;
  aggregationsMap: Record<string, Map<string, number>>;
};

const EMPTY_DATA = new Map<string, never>();

const useAttributeAggregationComparisonsData = (
  dimension: AttributeAggregationDimension
): UseAttributeAggregationComparisonsData => {
  const { currentSimDataId } = useCurrentSimDataId();
  const { comparisons } = useSchemaComparison();
  const { apiFilter } = useApiDataFilter();

  // 棒グラフにはフィルターで除外せずに全要素を表示したいため条件から除外する
  const filter = { ...apiFilter, [dimension]: undefined };
  const simDataIds = currentSimDataId ? [currentSimDataId, ...comparisons] : [];

  const results = useQueries({
    queries: simDataIds.map((simDataId: string) => {
      return {
        queryKey: ['attributeAggregation', simDataId, filter, dimension],
        queryFn: async ({
          signal,
        }: QueryFunctionContext): Promise<Map<string, number>> => {
          switch (dimension) {
            case 'gender':
            case 'age':
            case 'nationality':
              return dataApiClient
                .getAgentAggr(
                  simDataId,
                  filter,
                  'count',
                  dimension,
                  undefined,
                  signal
                )
                .then((items: Aggregation[]): Map<string, number> => {
                  return new Map(
                    items.map((item: Aggregation): [string, number] => {
                      return [item.key, item.value];
                    })
                  );
                });
            case 'end_activity_type':
            case 'longest_distance_mode':
              return dataApiClient
                .getTripAggr(
                  simDataId,
                  filter,
                  'count',
                  dimension,
                  undefined,
                  signal
                )
                .then((items: Aggregation[]): Map<string, number> => {
                  return new Map(
                    items.map((item: Aggregation): [string, number] => {
                      return [item.key, item.value];
                    })
                  );
                });
          }
        },
        meta: { dataType: 'others' },
      };
    }),
  });

  return {
    isLoading: results.some((result: UseQueryResult): boolean => {
      return result.isFetching;
    }),
    aggregationsMap: Object.fromEntries(
      simDataIds.map(
        (simDataId: string, index: number): [string, Map<string, number>] => {
          return [simDataId, results[index].data ?? EMPTY_DATA];
        }
      )
    ),
  };
};

export { useAttributeAggregationComparisonsData };
